import {useState, useEffect, useReducer, useCallback, useRef} from 'react'
import {api} from './api'
import {Album} from '../types'
import {useRequiredAuth} from '../stores/AuthStore'

export const ALBUMS_PAGE_SIZE = 25

// A single transient network blip (timeout / "Failed to fetch") on any one
// page used to silently drop that page's albums for the rest of the session,
// because maybeFetchMore advanced past the failed page and there was no retry.
// Auto-retry the *same* page a bounded number of times with backoff so a flaky
// request recovers without a full page refresh (AB#21157).
export const MAX_PAGE_RETRIES = 3
export const RETRY_BASE_DELAY_MS = 1000

/* *******************************  */

interface State {
  status: 'idle' | 'pending' | 'resolved' | 'rejected'
  data: Album[]
  totalCount: number
  hasMore: boolean
}

type Action =
  | {type: 'FETCH_INIT'}
  | {
      type: 'FETCH_SUCCESS'
      payload: {
        data: Album[]
        total: number
        nextURL: string
      }
    }
  | {type: 'FETCH_FAILURE'}
  | {type: 'RESET'}

const initialState: State = {
  status: 'idle',
  data: [],
  totalCount: 0,
  hasMore: false,
}

// Append only albums we haven't already accumulated, keyed on domain identity
// (.ID) rather than object reference. Guards against duplicate rows when a page
// overlaps the previous one on a mutating dataset, and against the StrictMode /
// client.ID-settling double-fetch of page 1 on mount (AB#21518). Module scope
// keeps the reducer's cognitive complexity under the S3776 threshold.
const mergeById = (existing: Album[], incoming: Album[]): Album[] => {
  const seen = new Set(existing.map((album) => album.ID))
  return [...existing, ...incoming.filter((album) => !seen.has(album.ID))]
}

const fetchReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'FETCH_INIT': {
      return {
        ...state,
        status: 'pending',
      }
    }
    case 'FETCH_SUCCESS': {
      return {
        ...state,
        status: 'resolved',
        data: mergeById(state.data, action.payload.data),
        totalCount: action.payload.total,
        hasMore: Boolean(action.payload.nextURL),
      }
    }
    case 'FETCH_FAILURE': {
      return {
        ...state,
        status: 'rejected',
      }
    }
    case 'RESET': {
      return initialState
    }
    default:
      throw new Error('Unhandled action')
  }
}

/* ******************************************** */
interface UseFetchAlbumsReturn extends State {
  maybeFetchMore: () => void
}

export const useFetchPaginatedAlbums = (): UseFetchAlbumsReturn => {
  const [pageNumber, setPageNumber] = useState(1)
  // Bumping retryCount re-runs the fetch effect for the *same* pageNumber.
  const [retryCount, setRetryCount] = useState(0)
  const {client} = useRequiredAuth()

  // Synchronous in-flight lock. The async `status` guard in maybeFetchMore does
  // not flip to 'pending' until a render -> effect -> dispatch('FETCH_INIT')
  // cycle *after* the advance, so a burst of scroll events all pass the stale
  // 'resolved' guard and each one increments pageNumber, skipping whole pages
  // (AB#21518). This ref blocks re-entry synchronously within that window. It
  // MUST be released on every settle path (the effect below) and cleared on the
  // tenant reset, or it strands and the hook stops advancing altogether — the
  // inverse failure documented in
  // docs/patterns/2026-06-24-suppression-guard-must-clear-on-every-reset.md.
  const isAdvancingRef = useRef(false)

  const [state, dispatch] = useReducer(fetchReducer, initialState)

  // Tenant-scoped reset. Albums, pageNumber, retryCount, and the advance lock
  // all belong to the active org/camp; on a client.ID change they must be
  // cleared so the new org starts from a clean page 1 and never renders the
  // previous org's albums or strands the lock (AB#21424 tenant-scope + lock
  // clear). Resetting during render — rather than in an effect — means the fetch
  // effect below runs once with pageNumber 1 for the new client, instead of
  // first firing a wasted fetch for the stale page number. Skipped on mount
  // (prevClientID starts equal to client.ID); a superseded in-flight request
  // from the old org is dropped by the fetch effect's didCancel cleanup.
  const [prevClientID, setPrevClientID] = useState(client.ID)
  if (client.ID !== prevClientID) {
    setPrevClientID(client.ID)
    dispatch({type: 'RESET'})
    setPageNumber(1)
    setRetryCount(0)
    isAdvancingRef.current = false
  }

  // Release the advance lock once the page settles. The condition guards a
  // single invariant: never clear the lock while a fetch is genuinely in flight
  // ('pending'). Every other status clears it — 'resolved'/'rejected' re-arm so
  // the next legitimate scroll can advance, and 'idle' (mount + post-RESET) is a
  // harmless no-op since the ref is already false there. Keep it written as
  // "anything but pending" rather than an explicit 'resolved'|'rejected' list,
  // so a future status value can't silently strand the lock by being forgotten.
  useEffect(() => {
    if (state.status !== 'pending') {
      isAdvancingRef.current = false
    }
  }, [state.status])

  useEffect(() => {
    let didCancel = false
    let retryTimer: ReturnType<typeof setTimeout> | undefined

    const fetchData = async (): Promise<void> => {
      dispatch({type: 'FETCH_INIT'})

      const response = await api.albums.fetchAlbums({
        pageNumber,
        pageSize: ALBUMS_PAGE_SIZE,
        clientID: client.ID,
      })

      // A superseded request (StrictMode remount or a dep change firing a new
      // fetch) must be ignored, not reported as an error. Folding didCancel
      // into the failure branch made a canceled request flip status to
      // 'rejected', which raced the live request and produced the intermittent
      // "There was a problem loading" screen on login. Match the cancel
      // handling used by the other fetch hooks: bail out, dispatch nothing.
      if (didCancel) return

      if (response.result === 'failure') {
        dispatch({type: 'FETCH_FAILURE'})

        // Retry the same page on a transient failure rather than leaving a
        // permanent gap. maybeFetchMore will not advance while we're rejected,
        // so the failed page is never skipped (AB#21157).
        if (retryCount < MAX_PAGE_RETRIES) {
          retryTimer = setTimeout(() => {
            if (!didCancel) {
              setRetryCount((count) => count + 1)
            }
          }, RETRY_BASE_DELAY_MS * 2 ** retryCount)
        }
      } else {
        dispatch({
          type: 'FETCH_SUCCESS',
          payload: {
            data: response.data.results,
            total: response.data.total,
            nextURL: response.data.nextURL,
          },
        })
      }
    }

    fetchData()

    return (): void => {
      didCancel = true
      if (retryTimer) {
        clearTimeout(retryTimer)
      }
    }
  }, [pageNumber, client.ID, retryCount])

  const maybeFetchMore = useCallback((): void => {
    // Synchronous re-entry guard: absorbs the scroll-event burst before the
    // async status can flip to 'pending' (AB#21518).
    if (isAdvancingRef.current) return

    // Only advance once the current page has resolved. Advancing while pending
    // double-fetches; advancing while rejected skips the failed page and drops
    // its albums for the rest of the session (AB#21157).
    if (state.status === 'resolved' && state.hasMore) {
      isAdvancingRef.current = true
      setRetryCount(0)
      setPageNumber((page) => page + 1)
    }
  }, [state.status, state.hasMore])

  return {
    status: state.status,
    data: state.data,
    hasMore: state.hasMore,
    totalCount: state.totalCount,
    maybeFetchMore,
  }
}
